import { Text } from '@organice/atoms'
import { useGetVisibleUserByIdLazyQuery } from '@organice/graphql'
import {
  UserOutlined,
  MailOutlined,
  MobileOutlined,
  PhoneOutlined,
} from '@ant-design/icons'
import IconBusiness from '@organice/icons/business.svg?react'
import IconUser from '@organice/icons/user-b-solid.svg?react'
import { Icon } from '@organice/atoms'
import React, { ComponentType } from 'react'
import styled from '@emotion/styled'
import { Button, Popover, Tag, theme, Tooltip } from 'antd'
import { useMeContext } from '@organice/contexts'
import { useTranslation } from 'react-i18next'
import { useKeyboardEventListeners } from '@organice/utils/hooks/use-keyboard-event-listeners'
import { Global, useTheme } from '@emotion/react'

type PersonProps = {
  user: {
    id?: number
    firstName?: string | null
    lastName?: string | null
    fullName?: string | null
    organisation?: {
      name: string
    } | null
  }
  children?: React.ReactNode
  isPrint?: boolean
  trigger?: ('click' | 'hover')[]
  onNameClicked?: () => void
}

export const Person: React.FC<PersonProps> = ({
  user,
  children,
  isPrint = false,
  trigger = ['click', 'hover'],
  onNameClicked,
}) => {
  const { id, firstName, lastName, fullName, organisation } = user
  const { t } = useTranslation()
  const theme = useTheme()
  // const [fetchUser, { data: userData, loading, error }] = useGetUserLazyQuery()
  const [fetchVisibleUser, { data: visibleUserData }] =
    useGetVisibleUserByIdLazyQuery()
  const {
    state: { isAdmin },
  } = useMeContext()

  React.useEffect(() => {
    if (!id) return
    fetchVisibleUser({
      variables: {
        id: id,
      },
    })
  }, [])

  const [visible, setVisible] = React.useState(false)
  /* Keyboard shortcuts */
  useKeyboardEventListeners([
    {
      keyName: 'Escape',
      keyHandler(e) {
        setVisible(false)
      },
    },
  ])

  const visibleUser = visibleUserData?.allUsers?.[0]

  const name = fullName
    ? fullName
    : firstName
      ? `${firstName} ${lastName}`
      : `${visibleUser?.firstName} ${visibleUser?.lastName}`

  if (children) {
    if (!visibleUser) {
      return (
        <Tooltip title={t('user.userHasBeenDeleted')}>
          <DisabledInlineWrapper>{children}</DisabledInlineWrapper>
        </Tooltip>
      )
    }

    return (
      <>
        {user ? (
          <>
            <Global
              styles={{
                '.ant-popover': {
                  minWidth: '20rem',
                },
                '.ant-popover-title': {
                  padding: '1.25rem 1.25rem',
                  backgroundColor: theme.grey3,
                  borderBottom: 'none',
                  color: '#000',
                },
                '.ant-popover-inner-content': {
                  padding: '1.25rem 1.25rem',
                },
              }}
            />
            <Popover
              placement="topLeft"
              title={
                <CardHeader>
                  <InfoRow>
                    <InfoValue>
                      <InfoSubRow className="strong large">{name}</InfoSubRow>
                      {visibleUser?.isDisabled ? (
                        <span
                          style={{
                            background: 'red',
                            color: 'white',
                            padding: '0 3px',
                          }}
                        >
                          {t('user.isDisabled')}
                        </span>
                      ) : null}
                    </InfoValue>
                  </InfoRow>
                </CardHeader>
              }
              content={
                <>
                  <ContactCard
                    contactInfo={{
                      isDisabled: Boolean(visibleUser?.isDisabled),
                      name: `${user?.firstName} ${user?.lastName}`,
                      organisation:
                        visibleUser?.organisation?.name || organisation?.name,
                      deplartment: visibleUser?.attributes?.department,
                      email: visibleUser?.publicEmail,
                      telephoneNumber: visibleUser?.attributes?.telephoneNumber,
                      mobile: visibleUser?.attributes?.mobile,
                    }}
                  />
                  {isAdmin ? (
                    <ButtonWrapper>
                      <Button
                        href={`/accounts/users/${user.id}`}
                        size="small"
                        type="primary"
                      >
                        {t('account.visitProfile')}
                      </Button>
                    </ButtonWrapper>
                  ) : null}
                </>
              }
              trigger={trigger}
            >
              {visibleUser?.isDisabled ? (
                <DisabledInlineWrapper>{children}</DisabledInlineWrapper>
              ) : (
                <>{children}</>
              )}
            </Popover>
          </>
        ) : (
          children
        )}
      </>
    )
  }

  if (isPrint) {
    return (
      <div>
        <InfoRow>
          <InfoValue>
            <InfoSubRow className="strong large">{name}</InfoSubRow>
            {visibleUser?.isDisabled ? (
              <span
                style={{
                  background: 'red',
                  color: 'white',
                  padding: '0 3px',
                }}
              >
                {t('user.isDisabled')}
              </span>
            ) : null}
          </InfoValue>
        </InfoRow>
        <ContactCard
          contactInfo={{
            isDisabled: Boolean(visibleUser?.isDisabled),
            name: `${user?.firstName} ${user?.lastName}`,
            organisation: visibleUser?.organisation?.name || organisation?.name,
            deplartment: visibleUser?.attributes?.department,
            email: visibleUser?.publicEmail,
            telephoneNumber: visibleUser?.attributes?.telephoneNumber,
            mobile: visibleUser?.attributes?.mobile,
          }}
        />
      </div>
    )
  }

  return (
    <PersonWrapper isDisabled={Boolean(visibleUser?.isDisabled)}>
      {user ? (
        <>
          <Global
            styles={{
              '.ant-popover': {
                minWidth: '20rem',
              },
              '.ant-popover-title': {
                padding: '1.25rem 1.25rem',
                backgroundColor: theme.grey3,
                borderBottom: 'none',
                color: '#000',
              },
              '.ant-popover-inner-content': {
                padding: '1.25rem 1.25rem',
              },
            }}
          />
          <Popover
            visible={visible}
            trigger={trigger}
            onOpenChange={visible => setVisible(visible)}
            placement="topLeft"
            title={
              <CardHeader>
                <InfoRow>
                  <InfoValue>
                    <InfoSubRow className="strong large">{`${user?.firstName} ${user?.lastName}`}</InfoSubRow>
                    {visibleUser?.isDisabled ? (
                      <span
                        style={{
                          background: 'red',
                          color: 'white',
                          padding: '0 3px',
                        }}
                      >
                        {t('user.isDisabled')}
                      </span>
                    ) : null}
                  </InfoValue>
                </InfoRow>
              </CardHeader>
            }
            content={
              <>
                <ContactCard
                  contactInfo={{
                    isDisabled: Boolean(visibleUser?.isDisabled),
                    name: `${user?.firstName} ${user?.lastName}`,
                    organisation:
                      visibleUser?.organisation?.name || organisation?.name,
                    deplartment: visibleUser?.attributes?.department,
                    email: visibleUser?.publicEmail,
                    telephoneNumber: visibleUser?.attributes?.telephoneNumber,
                    mobile: visibleUser?.attributes?.mobile,
                  }}
                />
                {isAdmin ? (
                  <ButtonWrapper>
                    <Button
                      href={`/accounts/users/${user.id}`}
                      size="small"
                      type="primary"
                    >
                      {t('account.visitProfile')}
                    </Button>
                  </ButtonWrapper>
                ) : null}
              </>
            }
          >
            <PersonName
              icon={<IconUser />}
              title={`${user?.firstName} ${user?.lastName}`}
              onClick={onNameClicked}
            >{`${user?.firstName} ${user?.lastName}`}</PersonName>
          </Popover>
        </>
      ) : (
        <PersonName
          icon={<IconUser />}
          title={`${user?.firstName} ${user?.lastName}`}
          onClick={onNameClicked}
        >{`${user?.firstName} ${user?.lastName}`}</PersonName>
      )}
    </PersonWrapper>
  )
}

type ContactCardProps = {
  contactInfo: {
    name: string
    isDisabled?: boolean
    organisation?: string | null
    deplartment?: string | null
    email?: string | null
    telephoneNumber?: string | null
    mobile?: string | null
  }
}

const ContactCard: React.FC<ContactCardProps> = ({ contactInfo }) => {
  const {
    name,
    isDisabled,
    organisation,
    deplartment,
    email,
    telephoneNumber,
    mobile,
  } = contactInfo
  return (
    <div>
      <InfoBlock isDisabled={isDisabled}>
        {organisation ? (
          <InfoRow>
            <InfoLabel>
              <Icon component={IconBusiness} />
            </InfoLabel>
            <InfoValue>
              <InfoSubRow className="strong">{organisation}</InfoSubRow>
              <InfoSubRow className="light small">{deplartment}</InfoSubRow>
            </InfoValue>
          </InfoRow>
        ) : null}

        {email ? (
          <InfoRow>
            <InfoLabel>
              <MailOutlined />
            </InfoLabel>
            <InfoValue>
              <a href={`mailto:${email}`}>{email}</a>
            </InfoValue>
          </InfoRow>
        ) : null}

        {telephoneNumber ? (
          <InfoRow>
            <InfoLabel>
              <PhoneOutlined />
            </InfoLabel>
            <InfoValue>
              <a href={`tel:${telephoneNumber}`}>{telephoneNumber}</a>
            </InfoValue>
          </InfoRow>
        ) : null}

        {mobile ? (
          <InfoRow>
            <InfoLabel>
              <MobileOutlined />
            </InfoLabel>
            <InfoValue>
              <a href={`tel:${mobile}`}>{mobile}</a>
            </InfoValue>
          </InfoRow>
        ) : null}
      </InfoBlock>
      {isDisabled ? <DisabledOverlay></DisabledOverlay> : null}
    </div>
  )
}

const ButtonWrapper = styled.div(({ theme }) => ({
  paddingTop: 20,
  paddingBottom: 10,
  //   display: 'flex',
  //   justifyContent: 'center',
}))

const CardHeader = styled.div(({ theme }) => ({
  //   borderBottom: `1px solid ${theme.grey3}`,
}))

const InfoBlock = styled.div<{ isDisabled?: boolean }>(({ isDisabled }) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 12,
  // make diagonal crossed lines overlay if isDisabled
  textDecoration: isDisabled ? 'line-through' : 'none',
  opacity: isDisabled ? 0.5 : 1,
}))

const InfoRow = styled.div(() => ({
  display: 'flex',
  gap: 5,
  alignItems: 'center',
}))

const InfoLabel = styled.div(() => ({
  marginRight: 5,
  alignSelf: 'flex-start',
}))

const InfoValue = styled.div(() => ({}))

const InfoSubRow = styled.div(({ theme }) => ({
  '&.large': {
    fontSize: theme.heading3Size,
  },
  '&.small': {
    fontSize: theme.fontSizeSm,
  },
  '&.strong': {
    fontWeight: 'bold',
  },
  '&.light': {
    color: theme.textColorLight,
  },
}))

const PersonWrapper = styled.div<{ isDisabled?: boolean }>(
  ({ isDisabled }) => ({
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    alignItems: 'center',
    // add style for isDisabled that is crossed out
    textDecoration: isDisabled ? 'line-through' : 'none',
  })
)

const PersonName = styled(Tag)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.grey2,
  backgroundColor: theme.grey6,
  padding: '0.25rem 0.5rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  lineHeight: 1.2,
  marginRight: 0,
  width: '100%',

  svg: {
    fontSize: 14,
    path: {
      fill: theme.grey2,
    },
  },
}))

const DisabledOverlay = styled.div(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  // backgroundImage: `url("${linesPattern}")`,
  opacity: 0.5,
}))

const DisabledInlineWrapper = styled.span(({ theme }) => ({
  textDecoration: 'line-through',
}))

const StyledUserIcon = styled(UserOutlined as ComponentType)(({ theme }) => ({
  cursor: 'pointer',
  //   background: theme.grey4,
  border: `1px solid ${theme.grey4}`,
  borderRadius: '50%',
  width: 18,
  height: 18,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  svg: {
    path: {
      fill: theme.grey4,
    },
  },
}))
